// pages/WorkspaceActivity.jsx — full workspace audit log (RBAC-scoped)
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { initials } from '../lib/constants';
import { Activity, Search } from 'lucide-react';

function timeAgo(d) {
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(d).toLocaleDateString();
}

const ACTION_TEXT = {
  created: 'created', moved: 'moved', completed: 'completed', reopened: 're-opened',
  priority_changed: 'changed priority', label_changed: 'changed label',
  due_date_changed: 'set due date', title_changed: 'renamed', description_updated: 'updated description',
  attachment_added: 'added attachment', attachment_removed: 'removed attachment',
  checklist_added: 'added checklist item', assignee_added: 'added an assignee',
  assignee_removed: 'removed an assignee', delete_requested: 'requested delete',
  delete_approved: 'approved delete', delete_rejected: 'rejected delete',
  commented: 'commented on', mentioned: 'mentioned someone in',
};

export default function WorkspaceActivity() {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/activity-log?limit=50&search=${encodeURIComponent(search)}`);
        setItems(data.activity || []); 
        setHasMore(!!data.has_more);
      } finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadMore = async () => {
    if (!items.length || busy) return;
    setBusy(true);
    try {
      const last = items[items.length - 1];
      const { data } = await api.get(`/activity-log?limit=50&search=${encodeURIComponent(search)}&before=${encodeURIComponent(last.created_at)}`);
      setItems((prev) => [...prev, ...(data.activity || [])]);
      setHasMore(!!data.has_more);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="workspace-activity-page">
      <div style={{ color: 'var(--trello-text)', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="flex items-center gap-3"><Activity size={24} /><h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Activity Log</h1></div>
          <p style={{ opacity: 0.85, marginTop: 4 }}>A live audit of every action across your workspace.</p>
        </div>
        <div style={{ position: 'relative', width: 300 }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            placeholder="Search activity..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="tm-input"
            style={{ width: '100%', paddingLeft: 40, borderRadius: 20, backgroundColor: 'rgba(255, 255, 255, 0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
          />
        </div>
      </div>

      <div className="tm-stat-card" style={{ borderLeft: 'none' }}>
        {items.length === 0 && !busy && <div style={{ fontSize: 13, color: 'var(--trello-text-muted)', textAlign: 'center', padding: 24 }}>No activity yet.</div>}
        <div>
          {items.map((a) => (
            <div key={a.id} className="flex items-start gap-3" style={{ padding: '10px 4px', borderTop: '1px solid var(--trello-border)' }} data-testid={`wsact-${a.id}`}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%',
                background: a.actor_color || 'var(--trello-text-muted)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>{initials({ full_name: a.actor_name })}</div>
              <div style={{ flex: 1, fontSize: 13 }}>
                <div>
                  <b>{a.actor_name || 'System'}</b>{' '}
                  <span>{ACTION_TEXT[a.action] || a.action.replace(/_/g, ' ')}</span>
                  {a.task_title && <> &quot;<Link to={`/projects/${a.proj_id}`} style={{ color: 'var(--trello-blue)' }}>{a.task_title}</Link>&quot;</>}
                  {a.project_name && <span style={{ color: 'var(--trello-text-muted)' }}> · in {a.project_name}</span>}
                </div>
                {(a.from_value || a.to_value) && (a.action !== 'commented') && (
                  <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 2 }}>
                    {a.from_value && <>from <b>{a.from_value}</b> </>}
                    {a.to_value && <>to <b>{String(a.to_value).slice(0, 80)}</b></>}
                  </div>
                )}
                {a.action === 'commented' && a.to_value && (
                  <div style={{ fontSize: 12, color: 'var(--trello-text)', marginTop: 2, fontStyle: 'italic' }}>“{String(a.to_value).slice(0, 160)}”</div>
                )}
                <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 3 }}>
                  {timeAgo(a.created_at)}
                  {a.ip_address && <span style={{ marginLeft: 8, opacity: 0.7 }}> · {a.ip_address}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
        {hasMore && (
          <button className="tm-btn-ghost" onClick={loadMore} disabled={busy} data-testid="wsact-load-more"
            style={{ width: '100%', justifyContent: 'center', padding: 10, marginTop: 8 }}>
            {busy ? 'Loading…' : 'Load older activity'}
          </button>
        )}
      </div>
    </div>
  );
}
